import 'katex/dist/katex.min.css';

import type { Components } from 'react-markdown';

import { useMemo } from 'react';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import ReactMarkdown from 'react-markdown';
import { toMathMarkdown, holdBackOpenMath } from '@classroom-ai/types';

// ----------------------------------------------------------------------

export type MathMarkdownProps = {
  children: string | null | undefined;
  /** Element mapping passed straight to react-markdown. */
  components?: Components;
  /** True while the text is still streaming in: a trailing, not-yet-closed `$` span
   *  is held back so half-written LaTeX never flashes on screen. */
  streaming?: boolean;
};

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [[rehypeKatex, { throwOnError: false, strict: false }]] as never[];

/**
 * GFM Markdown with LaTeX math (epic #290). Dollars are pre-classified by the shared
 * `splitMath` (currency like "$2.50" stays text), then remark-math + rehype-katex
 * render each `$…$` / `$$…$$` span.
 */
export function MathMarkdown({ children, components, streaming = false }: MathMarkdownProps) {
  const source = useMemo(() => {
    const raw = children ?? '';
    return toMathMarkdown(streaming ? holdBackOpenMath(raw) : raw);
  }, [children, streaming]);

  return (
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={components}>
      {source}
    </ReactMarkdown>
  );
}
